import { ChevronRight, Plus } from "lucide-react";
import { useEffect, useMemo, useState, type FC } from "react";

import { MetaLabel } from "@/components/surface";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import { ForeignNationalsAPI } from "../../../../api/foreignNationals";
import type { ForeignNationalDependent } from "../../../../api/types/foreignNationalDependent";
import { SectionCard } from "../../components/SectionCard";
import {
  dependentName,
  dependentSubtitle,
  sortDependents
} from "../../lib/dependents";
import { initialsFromName } from "../../lib/format";

const PREVIEW_COUNT = 4;

type Props = {
  clientId: string;
  onOpenDependents: (dependentId?: string) => void;
};

/** The first few dependents with a link into the Dependents section. */
export const DependentsPreviewCard: FC<Props> = ({
  clientId,
  onOpenDependents
}) => {
  const [dependents, setDependents] = useState<ForeignNationalDependent[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    ForeignNationalsAPI.listDependents(clientId)
      .then((list) => {
        if (!cancelled) setDependents(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setDependents([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const preview = useMemo(
    () => sortDependents(dependents).slice(0, PREVIEW_COUNT),
    [dependents]
  );

  return (
    <SectionCard
      title="Dependents"
      flush
      className="h-full"
      action={
        <Button
          variant="ghost"
          size="xs"
          className="text-muted-foreground hover:text-foreground"
          onClick={() => onOpenDependents()}
        >
          <Plus /> Add
        </Button>
      }
    >
      <div className="flex flex-col divide-y divide-border">
        {isLoading && dependents.length === 0 ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="flex items-center gap-3 px-3 py-2"
              aria-hidden
            >
              <Skeleton className="size-6 rounded-md" />
              <div className="flex flex-1 flex-col gap-1">
                <Skeleton className="h-3 w-32 rounded-sm" />
                <Skeleton className="h-3 w-20 rounded-sm" />
              </div>
            </div>
          ))
        ) : preview.length === 0 ? (
          <button
            type="button"
            className="flex w-full items-center gap-3 px-3 py-2 text-left text-base transition-colors hover:bg-fill-1"
            onClick={() => onOpenDependents()}
          >
            <Plus className="size-3.5 text-muted-foreground" />
            <span className="flex-1">Add dependents</span>
            <ChevronRight className="size-3 text-fg-3" />
          </button>
        ) : (
          <>
            {preview.map((d) => {
              const name = dependentName(d) || "Dependent";
              const status = (d.current_immigration_status ?? "").trim();
              return (
                <button
                  key={d.dependent_id}
                  type="button"
                  className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-fill-1"
                  onClick={() => onOpenDependents(d.dependent_id)}
                >
                  <Avatar size="sm">
                    <AvatarFallback className="text-xs">
                      {initialsFromName(name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-base leading-5 font-medium">
                      {name}
                    </span>
                    <span className="truncate text-xs leading-4 text-muted-foreground">
                      {dependentSubtitle(d)}
                    </span>
                  </div>
                  {status ? (
                    <MetaLabel className="max-w-30 truncate">
                      {status}
                    </MetaLabel>
                  ) : null}
                  <ChevronRight className="size-3 shrink-0 text-fg-3" />
                </button>
              );
            })}
            {dependents.length > preview.length ? (
              <button
                type="button"
                className="px-3 py-1.5 text-left text-xs text-muted-foreground hover:text-foreground"
                onClick={() => onOpenDependents()}
              >
                View all {dependents.length} dependents
              </button>
            ) : null}
          </>
        )}
      </div>
    </SectionCard>
  );
};
